import React from 'react';
import {
	Box,
	Button,
	IconButton,
	Stack,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import dayjs from 'dayjs';
import MobileCreateFab from '../../../Components/MobileCreateFab';

const PRESETS = [
	{ value: 'crew_week', label: 'Crew week' },
	{ value: 'jobs_week', label: 'Jobs week' },
	{ value: 'today', label: 'Today' },
];

const ScheduleToolbar = ({
	preset,
	onPresetChange,
	anchorDate,
	onAnchorChange,
	range,
	isManager,
	onCreate,
}) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const isToday = preset === 'today';

	const rangeLabel = isToday
		? dayjs(anchorDate).format('ddd, MMM D')
		: `${dayjs(range?.from).format('MMM D')} - ${dayjs(range?.to).format(
				'MMM D, YYYY',
			)}`;

	const shift = (delta) => {
		if (isToday) {
			onAnchorChange(dayjs(anchorDate).add(delta, 'day'));
		} else {
			onAnchorChange(dayjs(anchorDate).add(delta * 7, 'day'));
		}
	};

	return (
		<>
			<Stack spacing={2} sx={{ mb: 2.5 }}>
				<Stack
					direction={{ xs: 'column', sm: 'row' }}
					spacing={1.5}
					alignItems={{ xs: 'stretch', sm: 'center' }}
					justifyContent="space-between"
				>
					<ToggleButtonGroup
						exclusive
						size={isMobile ? 'medium' : 'small'}
						value={preset}
						onChange={(_, next) => {
							if (next) onPresetChange(next);
						}}
						sx={{
							flexWrap: 'wrap',
							'& .MuiToggleButton-root': {
								px: { xs: 1.5, md: 2 },
								py: { xs: 1, md: 0.75 },
								fontWeight: 600,
								textTransform: 'none',
								flex: isMobile ? 1 : undefined,
							},
						}}
					>
						{PRESETS.map((item) => (
							<ToggleButton key={item.value} value={item.value}>
								{item.label}
							</ToggleButton>
						))}
					</ToggleButtonGroup>

					{isManager && !isMobile && (
						<Button
							variant="contained"
							startIcon={<AddIcon />}
							onClick={onCreate}
							sx={{ alignSelf: 'auto' }}
						>
							Add assignment
						</Button>
					)}
				</Stack>

				<Stack
					direction="row"
					alignItems="center"
					justifyContent="space-between"
					spacing={1}
				>
					<IconButton
						aria-label={isToday ? 'Previous day' : 'Previous week'}
						onClick={() => shift(-1)}
						size="large"
					>
						<ChevronLeftIcon />
					</IconButton>
					<Box sx={{ textAlign: 'center', minWidth: 0 }}>
						<Typography variant="subtitle1" fontWeight={700} noWrap>
							{isToday ? 'Today' : 'This week'}
						</Typography>
						<Typography variant="body2" color="text.secondary" noWrap>
							{rangeLabel}
						</Typography>
						<Button
							size="small"
							onClick={() => onAnchorChange(dayjs())}
							sx={{ mt: 0.25, textTransform: 'none' }}
						>
							Jump to today
						</Button>
					</Box>
					<IconButton
						aria-label={isToday ? 'Next day' : 'Next week'}
						onClick={() => shift(1)}
						size="large"
					>
						<ChevronRightIcon />
					</IconButton>
				</Stack>
			</Stack>
			{isManager ? (
				<MobileCreateFab label="Add assignment" onClick={onCreate} />
			) : null}
		</>
	);
};

export default ScheduleToolbar;
